<template>
  <div class="ticker-container">
    <div class="ticker-wrapper" ref="wrapper">
      <div class="ticker-content" ref="content">
        <!-- Original items -->
        <div 
          v-for="(item, index) in data" 
          :key="'original-' + index"
          class="ticker-item"
        >


          <a :href="item.link" class="ticker-link">
            <span class="timeago">{{ getTimeAgo(item.created) }}</span>
            <img class="qwhiteicon" v-if="item.img && item.img.filename" :src="item.img.filename" :alt="item.title"  onerror="this.onerror=null;this.src='/assets/icons/intro.png'"/>
            <span class="ticker-text">{{ item.title }}</span>
          </a>
          
          <!-- Info button that triggers popper -->
          <button v-if="item.detail.length > 0"
  class="info-button"
  @click.stop="togglePopper(item, $event)"
  @mouseenter="showPopper(item, $event)"
  @mouseleave="scheduleHidePopper"
>
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
</button>
          
          <span v-if="index < data.length - 1" class="separator">{{ separator }}</span>
        </div>
        
        <!-- Duplicate items -->
        <div 
          v-for="(item, index) in data" 
          :key="'duplicate-' + index"
          class="ticker-item"
        >
          <a :href="item.link" class="ticker-link">
            <span class="timeago">{{ getTimeAgo(item.created) }}</span>
            <img v-if="item.img && item.img.filename" :src="item.img.filename" :alt="item.title" />
            <span class="ticker-text">{{ item.title }}</span>
          </a>
          
          <!-- Info button that triggers popper -->
          <button 
            class="info-button"
            @click.stop="togglePopper(item, $event)"
            @mouseenter="showPopper(item, $event)"
            @mouseleave="scheduleHidePopper"
          >
            ℹ️
          </button>
          
          <span v-if="index < data.length - 1" class="separator">{{ separator }}</span>
        </div>
      </div>
    </div>
    
    <!-- Popper container - Rendered outside ticker content -->
    <div 
      v-if="popperVisible" 
      class="popper-container"
      :style="popperStyle"
      @mouseenter="cancelHidePopper"
      @mouseleave="hidePopper"
    >
      <div class="popper-arrow" :class="popperArrowClass"></div>
      <div class="popper-content">
        <div class="popper-header">
          <h3>{{ popperItem ? popperItem.title : '' }}</h3>
          <button class="popper-close" @click="hidePopper">×</button>
        </div>
        
        <div class="popper-body">
          <p v-if="popperItem && popperItem.desc">{{ popperItem.desc }}</p>
          <p v-if="popperItem && popperItem.detail">{{ popperItem.detail }}</p>
        </div>
        
        <!-- <div class="popper-footer">
        
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TickerTape',
  
  props: {
    data: {
      type: Array,
      required: true,
      default: function() {
        return []
      }
    },
    separator: {
      type: String,
      default: '•'
    },
    speed: {
      type: Number,
      default: 50
    }
  },
  
  data: function() {
    return {
      animationId: null,
      isPlaying: true,
      currentPosition: 0,
      isMobile: false,
      singleSetWidth: 0,
      isInitialized: false,
      resizeObserver: null,
      // Popper
      popperVisible: false,
      popperItem: null,
      popperStyle: {},
      popperArrowClass: '',
      popperTimeout: null,
      currentButton: null,
      // Prevent unnecessary resets
      lastDataString: '',
      animationFrameActive: false
    }
  },
  
  mounted: function() {
    var self = this
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    // Store initial data string for comparison
    this.lastDataString = JSON.stringify(this.data)
    
    this.$nextTick(function() {
      self.initAnimation()
      self.setupResizeObserver()
      self.setupInteractionEvents()
    })
  },
  
  beforeDestroy: function() {
    this.cleanup()
  },
  
  activated: function() {
    if (this.isInitialized && !this.animationId) {
      this.startAnimation()
    }
  },
  
  deactivated: function() {
    this.stopAnimation()
    this.hidePopper()
  },
  
  methods: {
    // Popper methods
    showPopper: function(item, event) {
      var self = this
      
      if (this.popperTimeout) {
        clearTimeout(this.popperTimeout)
        this.popperTimeout = null
      }
      
      this.popperItem = item
      this.currentButton = event.currentTarget
      this.popperVisible = true
      
      this.$nextTick(function() {
        self.calculatePopperPosition()
      })
    },
    
    togglePopper: function(item, event) {
      if (this.popperVisible && this.popperItem === item) {
        this.hidePopper()
      } else {
        this.showPopper(item, event)
      }
    },
    
    calculatePopperPosition: function() {
      if (!this.currentButton) return
      
      var rect = this.currentButton.getBoundingClientRect()
      var viewportWidth = window.innerWidth
      var viewportHeight = window.innerHeight
      
      var popperWidth = 280
      var popperHeight = 200
      
      var top, left
      
      var spaceBelow = viewportHeight - rect.bottom
      var spaceAbove = rect.top
      
      if (spaceBelow >= popperHeight + 10) {
        top = rect.bottom + 10
        this.popperArrowClass = 'arrow-up'
      } else if (spaceAbove >= popperHeight + 10) {
        top = rect.top - popperHeight - 10
        this.popperArrowClass = 'arrow-down'
      } else {
        top = rect.bottom + 10
        this.popperArrowClass = 'arrow-up'
      }
      
      left = rect.left + (rect.width / 2) - (popperWidth / 2)
      left = Math.max(10, Math.min(left, viewportWidth - popperWidth - 10))
      
      this.popperStyle = {
        position: 'fixed',
        top: top + 'px',
        left: left + 'px',
        zIndex: 10000
      }
    },
    
    scheduleHidePopper: function() {
      var self = this
      this.popperTimeout = setTimeout(function() {
        self.hidePopper()
      }, 200)
    },
    
    cancelHidePopper: function() {
      if (this.popperTimeout) {
        clearTimeout(this.popperTimeout)
        this.popperTimeout = null
      }
    },
    
    hidePopper: function() {
      this.popperVisible = false
      this.popperItem = null
      this.currentButton = null
      if (this.popperTimeout) {
        clearTimeout(this.popperTimeout)
        this.popperTimeout = null
      }
    },
    
    initAnimation: function() {
      var content = this.$refs.content
      var wrapper = this.$refs.wrapper
      
      if (!content || !wrapper) return
      
      // Get full content width and calculate single set width
      var fullWidth = content.scrollWidth
      this.singleSetWidth = fullWidth / 2
      
      if (this.singleSetWidth === 0) {
        setTimeout(function() {
          if (!this.isInitialized) {
            this.initAnimation()
          }
        }.bind(this), 100)
        return
      }
      
      // Only reset position if not initialized
      if (!this.isInitialized) {
        this.currentPosition = 0
        content.style.transform = 'translateX(0px)'
      }
      
      this.isInitialized = true
      this.startAnimation()
    },
    
    startAnimation: function() {
      var self = this
      
      if (this.animationId) {
        return
      }
      
      var lastTimestamp = null
      var lastPosition = this.currentPosition
      
      function animate(timestamp) {
        if (!lastTimestamp) {
          lastTimestamp = timestamp
          self.animationId = requestAnimationFrame(animate)
          return
        }
        
        if (self.isPlaying && self.isInitialized && self.singleSetWidth > 0) {
          // Calculate time difference in seconds
          var deltaTime = Math.min(0.033, (timestamp - lastTimestamp) / 1000)
          lastTimestamp = timestamp
          
          // Move left based on speed (pixels per second)
          var newPosition = lastPosition - (self.speed * deltaTime)
          
          // Check if we've scrolled past one full set
          while (Math.abs(newPosition) >= self.singleSetWidth) {
            newPosition = newPosition + self.singleSetWidth
          }
          
          // Update position
          lastPosition = newPosition
          self.currentPosition = newPosition
          
          // Apply transform
          if (self.$refs.content) {
            self.$refs.content.style.transform = 'translateX(' + newPosition + 'px)'
          }
        } else if (!self.isPlaying) {
          // When paused, update timestamp to prevent delta time jump
          lastTimestamp = timestamp
          lastPosition = self.currentPosition
        }
        
        self.animationId = requestAnimationFrame(animate)
      }
      
      this.animationId = requestAnimationFrame(animate)
    },
    
    stopAnimation: function() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
    },
    
    setupResizeObserver: function() {
      var self = this
      
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(function() {
          if (self.$refs.content && self.isInitialized) {
            var newWidth = self.$refs.content.scrollWidth / 2
            if (newWidth !== self.singleSetWidth && newWidth > 0) {
              self.singleSetWidth = newWidth
              // Keep current position within bounds without resetting
              while (Math.abs(self.currentPosition) >= self.singleSetWidth) {
                self.currentPosition = self.currentPosition + self.singleSetWidth
              }
            }
          }
        })
        
        if (this.$refs.content) {
          this.resizeObserver.observe(this.$refs.content)
        }
      }
    },
    
    setupInteractionEvents: function() {
      var wrapper = this.$refs.wrapper
      if (!wrapper) return
      
      wrapper.addEventListener('mouseenter', this.handleMouseEnter)
      wrapper.addEventListener('mouseleave', this.handleMouseLeave)
      wrapper.addEventListener('touchstart', this.handleTouchStart, { passive: true })
      wrapper.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    },
    
    removeInteractionEvents: function() {
      var wrapper = this.$refs.wrapper
      if (!wrapper) return
      
      wrapper.removeEventListener('mouseenter', this.handleMouseEnter)
      wrapper.removeEventListener('mouseleave', this.handleMouseLeave)
      wrapper.removeEventListener('touchstart', this.handleTouchStart)
      wrapper.removeEventListener('touchend', this.handleTouchEnd)
    },
    
    handleMouseEnter: function() {
      this.isPlaying = false
    },
    
    handleMouseLeave: function() {
      this.isPlaying = true
    },
    
    handleTouchStart: function() {
      this.isPlaying = false
    },
    
    handleTouchEnd: function() {
      this.isPlaying = true
    },
    
    cleanup: function() {
      this.stopAnimation()
      this.removeInteractionEvents()
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }
      this.isInitialized = false
      this.hidePopper()
    },
    
    getTimeAgo: function(createdDate) {
      if (!createdDate) return ''
      
      var now = new Date()
      var created = new Date(createdDate)
      
      if (isNaN(created.getTime())) return ''
      
      var diffInSeconds = Math.floor((now - created) / 1000)
      
      if (diffInSeconds < 60) return diffInSeconds + 's'
      
      var diffInMinutes = Math.floor(diffInSeconds / 60)
      if (diffInMinutes < 60) return diffInMinutes + 'm'
      
      var diffInHours = Math.floor(diffInMinutes / 60)
      if (diffInHours < 24) return diffInHours + 'h'
      
      var diffInDays = Math.floor(diffInHours / 24)
      if (diffInDays < 7) return diffInDays + 'd'
      
      var diffInWeeks = Math.floor(diffInDays / 7)
      if (diffInWeeks < 4) return diffInWeeks + 'w'
      
      var diffInMonths = Math.floor(diffInDays / 30)
      if (diffInMonths < 12) return diffInMonths + 'mo'
      
      var diffInYears = Math.floor(diffInDays / 365)
      return diffInYears + 'y'
    },
    
    resetAndStart: function() {
      // Only reset if component is still active and data actually changed
      if (!this.$refs.content) return
      
      // Check if data actually changed
      var currentDataString = JSON.stringify(this.data)
      if (currentDataString === this.lastDataString) {
        return // No change, don't reset
      }
      
      this.lastDataString = currentDataString
      
      this.stopAnimation()
      this.currentPosition = 0
      if (this.$refs.content) {
        this.$refs.content.style.transform = 'translateX(0px)'
      }
      
      this.singleSetWidth = this.$refs.content.scrollWidth / 2
      this.startAnimation()
    }
  },
  
  watch: {
    data: {
      handler: function(newVal, oldVal) {
        var self = this
        // Only reset if data actually changed
        if (this.isInitialized && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$nextTick(function() {
            self.resetAndStart()
          })
        }
      },
      deep: true
    },
    speed: function(newVal, oldVal) {
      if (this.isInitialized && newVal !== oldVal) {
        this.resetAndStart()
      }
    }
  }
}
</script>

<style scoped>
.ticker-container {
  width: 100%;
  overflow: hidden;
  background: linear-gradient(90deg, #1e3c72, #2a5298);
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.ticker-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
}

.timeago {
  font-size: 8px;
  font-weight: 300;
  margin-left: 5px;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 12px;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
}

.ticker-content {
  display: inline-flex;
  white-space: nowrap;
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  transform: translateZ(0);
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  padding: 12px 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  gap: 12px;
}

.ticker-link {
  color: #ffffff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
  cursor: pointer;
}

.ticker-link:active {
  color: #ffd700;
}

/* Image styling - NO circular background */
.ticker-link img {
  width: 20px;
  height: 20px;
  object-fit: cover;
  pointer-events: none;
  flex-shrink: 0;
  border-radius: 0;
}

.ticker-text {
  display: inline-block;
  white-space: nowrap;
}

/* Info Button - Styled but no background circle */
.info-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 4px;
  padding: 0;
  flex-shrink: 0;
  color: white;
}

.info-button:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.info-button:active {
  transform: scale(0.95);
}

.separator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffd700;
  font-weight: bold;
  font-size: 18px;
  flex-shrink: 0;
  margin: 0;
  line-height: 1;
}

/* Popper Container */
.popper-container {
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  animation: popperFadeIn 0.2s ease;
}

.popper-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  left: 50%;
  transform: translateX(-50%);
}

.popper-arrow.arrow-up {
  top: -8px;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent white transparent;
}

.popper-arrow.arrow-down {
  bottom: -8px;
  border-width: 8px 8px 0 8px;
  border-color: white transparent transparent transparent;
}

.popper-content {
  padding: 0;
  min-width: 260px;
  max-width: 280px;
}

.popper-header {
  background: #f7fafc;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
}

.popper-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e3c72;
}

.popper-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #718096;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popper-close:hover {
  color: #1e3c72;
}

.popper-body {
  padding: 16px;
}

.popper-body p {
  margin: 0 0 12px 0;
  color: #4a5568;
  font-size: 13px;
  line-height: 1.5;
}

.popper-meta {
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.popper-time {
  font-size: 11px;
  color: #718096;
}

.popper-footer {
  padding: 12px 16px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.popper-link {
  display: inline-block;
  padding: 6px 12px;
  background: #1e3c72;
  color: white;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.popper-link:hover {
  background: #2a5298;
  transform: translateX(2px);
}

/* Hover effects */
.ticker-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.ticker-item:hover .ticker-link {
  color: #ffd700;
}

/* Responsive design */
@media (max-width: 768px) {
  .ticker-item {
    padding: 10px 0;
    font-size: 14px;
    gap: 10px;
  }
  
  .separator {
    font-size: 16px;
  }
  
  .ticker-link {
    gap: 6px;
  }
  
 .ticker-link img {
    width: 18px;
    height: 18px;
  } 
  
    .info-button {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .timeago {
    font-size: 9px;
    padding: 2px 5px;
    min-width: 28px;
  }
}

/* Fade effect on edges */
.ticker-wrapper::before,
.ticker-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50px;
  z-index: 1;
  pointer-events: none;
}

.ticker-wrapper::before {
  left: 0;
  background: linear-gradient(to right, #1e3c72, transparent);
}

.ticker-wrapper::after {
  right: 0;
  background: linear-gradient(to left, #2a5298, transparent);
}

@media (max-width: 768px) {
  .ticker-wrapper::before,
  .ticker-wrapper::after {
    width: 30px;
  }
}

@keyframes popperFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>